'use client'
import { FormMode } from '@/lib/common-models'
import { TypeOf, ZodSchema } from 'zod'
import { ControllerRenderProps, Path, useFormContext } from 'react-hook-form'
import { FormControl, FormItem, FormMessage } from '@/components/ui/form/form'
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@/components/ui/toggle-group/toggle-group'
import styles from '@/components/ui/fields/radio-button-card-input/radio-button-styles.module.css'
import { Circle } from 'lucide-react'
import { createContext, useContext } from 'react'
import { HiddenInput } from '@/components/ui/fields/hidden-input'

// TO-DO: Probably unify the contexts
const RadioButtonContext = createContext<ControllerRenderProps | null>(null)
const InputContext = createContext<string | null>(null)
const ReadContext = createContext<boolean>(false)

export const RadioIcon = ({ ...props }: { [key: string]: unknown }) => {
  const field = useContext(RadioButtonContext)
  const value = useContext(InputContext)
  const isRead = useContext(ReadContext)

  if (!field) {
    return <></>
  }
  if (isRead) {
    return (
      <HiddenInput
        name={field.name}
        transactionName={`components.${field.name}`}
      />
    )
  }

  if (!value) {
    return <></>
  }
  return (
    <>
      <input
        type="radio"
        name={field.name}
        className="hidden"
        {...props}
        value={value}
        checked={field.value === value}
        onChange={() => {
          if (field.value !== value) {
            field.onChange(value)
          }
        }}
      />
      <svg
        viewBox="0 0 20 20"
        className="radio-icon m-2 size-5 min-h-5 min-w-5 fill-none"
      >
        <Circle
          width="20px"
          height="20px"
          className="stroke-slate-300 stroke-2"
        ></Circle>
        <path
          d="M10,7 C8.34314575,7 7,8.34314575 7,10 C7,11.6568542 8.34314575,13 10,13 C11.6568542,13 13,11.6568542 13,10 C13,8.34314575 11.6568542,7 10,7 Z"
          className="inner stroke-blue-500 stroke-[0.36rem] data-[state=off]:fill-transparent data-[state=on]:fill-blue-500"
          fillOpacity="0.5"
        ></path>
        <path
          d="M10,1 L10,1 L10,1 C14.9705627,1 19,5.02943725 19,10 L19,10 L19,10 C19,14.9705627 14.9705627,19 10,19 L10,19 L10,19 C5.02943725,19 1,14.9705627 1,10 L1,10 L1,10 C1,5.02943725 5.02943725,1 10,1 L10,1 Z"
          className="outer stroke-blue-500 stroke-1"
        ></path>
      </svg>
    </>
  )
}

interface RadioButtonCardProps {
  /**
   * Value of the radio button
   */
  value: string
  /**
   * Content to be displayed inside the radio button card
   */
  children: React.ReactNode
  disabled?: boolean
}

export const RadioButtonCard: React.FC<RadioButtonCardProps> = ({
  value,
  children,
  disabled = false,
}) => {
  const isRead = useContext(ReadContext)
  const field = useContext(RadioButtonContext)
  if (!field) {
    return <></>
  }
  if (isRead) {
    if (field.value === value) {
      return (
        <div className="m-3 flex size-fit w-full flex-row rounded-3xl border border-gray-300 p-6 py-3 md:m-4">
          {children}
        </div>
      )
    } else {
      return <></>
    }
  }
  return (
    <InputContext.Provider value={value}>
      <ToggleGroupItem
        value={value}
        className={styles.toggleGroupItem}
        variant="default"
        size="lg"
        disabled={disabled}
      >
        {children}
      </ToggleGroupItem>
    </InputContext.Provider>
  )
}

interface RadioButtonWrapperCreateProps<
  T extends ZodSchema,
  K extends Path<TypeOf<T>>,
> {
  field: ControllerRenderProps<TypeOf<T>, K>
  children: React.ReactNode
}
const RadioButtonWrapperCreate = <
  T extends ZodSchema,
  K extends Path<TypeOf<T>>,
>({
  field,
  children,
}: RadioButtonWrapperCreateProps<T, K>) => {
  const { trigger } = useFormContext<T>()
  const setAttribute = (value: string) => {
    if (value && value !== field.value) {
      field.onChange(value)
      trigger(field.name)
    }
  }

  return (
    <FormItem className="animate-fade-in space-y-3">
      <FormControl>
        <ToggleGroup
          type="single"
          defaultValue={field.value}
          onValueChange={setAttribute}
        >
          <RadioButtonContext.Provider value={field}>
            {children}
          </RadioButtonContext.Provider>
        </ToggleGroup>
      </FormControl>
      <FormMessage />
    </FormItem>
  )
}

const RadioButtonWrapperRead = <
  T extends ZodSchema,
  K extends Path<TypeOf<T>>,
>({
  children,
  field,
}: RadioButtonWrapperCreateProps<T, K>) => {
  return (
    <ReadContext.Provider value={true}>
      <ToggleGroup type="single" defaultChecked={true} value={field.value}>
        <RadioButtonContext.Provider value={field}>
          {children}
        </RadioButtonContext.Provider>
      </ToggleGroup>
    </ReadContext.Provider>
  )
}

// TO-DO: Improve types using unions
interface RadioButtonRawCardInputProps<
  T extends ZodSchema,
  K extends Path<TypeOf<T>>,
> {
  /**
   * Mode to render the component in, it can be `CREATE`, `READ` or `UPDATE`
   */
  mode: FormMode

  /**
   * Radio Button Inputs, Typically a Radio Button Card Component
   */
  children?: React.ReactNode

  /**
   * React Hook Form Field
   */
  field: ControllerRenderProps<TypeOf<T>, K>
}

export const RadioButtonRawCardInput = <
  T extends ZodSchema,
  K extends Path<TypeOf<T>>,
>({
  mode,
  children,
  ...props
}: RadioButtonRawCardInputProps<T, K>) => {
  switch (mode) {
    case FormMode.CREATE:
      return (
        <RadioButtonWrapperCreate {...props}>
          {children}
        </RadioButtonWrapperCreate>
      )
    case FormMode.READ:
      return (
        <RadioButtonWrapperRead {...props}>{children}</RadioButtonWrapperRead>
      )
    case FormMode.UPDATE:
      return <div>UPDATE</div>
  }
}
